import { CALL_OUTCOMES, type CallOutcome } from '@beta-assur/shared';
import { Phone } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Field } from '../components/page';
import { ActionError } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input, Select, Textarea } from '../components/ui/input';
import { day, instant, money } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiMutation } from '../lib/query';
import { useTenant } from '../lib/referentials';
import type { EscalationDetail } from '../lib/types';

// Tâche d'appel (BA-092) : numéro cliquable, contexte (relances, paiements), issue de l'appel,
// promesse de paiement, rappel programmé. Le téléphone reste humain : la plateforme prépare et
// trace l'appel.

export function CallCard({ escalation }: { escalation: EscalationDetail }) {
  const { t, language } = useI18n();
  const tz = useTenant().data?.timezone;
  const [outcome, setOutcome] = useState<CallOutcome>('no_answer');
  const [promiseDate, setPromiseDate] = useState('');
  const [callBack, setCallBack] = useState('');
  const [note, setNote] = useState('');
  const act = useApiMutation<Record<string, unknown>, EscalationDetail>(
    'POST',
    `/v1/escalations/${escalation.id}/actions`,
    {
      invalidate: ['/v1/escalations', '/v1/installments', '/v1/dashboard'],
      onSuccess: () => {
        setNote('');
        setCallBack('');
      },
    },
  );
  function submit(event: FormEvent) {
    event.preventDefault();
    act.mutate({
      action: 'call',
      outcome,
      ...(outcome === 'promise' && promiseDate ? { promiseDate } : {}),
      ...(callBack ? { callBackAt: new Date(callBack).toISOString() } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  }
  const sent = escalation.messages.filter((m) => m.direction === 'out');
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.calls.title}</CardTitle>
        {escalation.lastCallOutcome ? (
          <Badge tone="info">
            {t.calls.outcomes[escalation.lastCallOutcome]} ({escalation.callAttempts})
          </Badge>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {escalation.phone ? (
          <a
            href={`tel:${escalation.phone}`}
            className="inline-flex w-fit items-center gap-2 rounded-lg border border-border px-3 py-2 text-lg font-semibold text-primary hover:bg-muted"
            dir="ltr"
          >
            <Phone className="h-5 w-5" aria-hidden />
            {escalation.phone}
          </a>
        ) : (
          <p className="text-sm text-destructive">{t.calls.noPhone}</p>
        )}
        <ul className="grid gap-1 text-sm text-muted-foreground">
          <li>{t.calls.remindersSent(sent.length)}</li>
          <li>
            {escalation.payments.length
              ? t.calls.payments(
                  escalation.payments
                    .map((p) => `${money(p.amount, language)} (${day(p.confirmedAt, language)})`)
                    .join(', '),
                )
              : t.calls.noPayment}
          </li>
          {escalation.callBackAt ? (
            <li>
              {t.calls.callBackAt} {instant(escalation.callBackAt, tz, language)}
            </li>
          ) : null}
        </ul>
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t.calls.outcome}>
            <Select value={outcome} onChange={(e) => setOutcome(e.target.value as CallOutcome)}>
              {CALL_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {t.calls.outcomes[o]}
                </option>
              ))}
            </Select>
          </Field>
          {outcome === 'promise' ? (
            <Field label={t.calls.promiseDate}>
              <Input
                type="date"
                required
                value={promiseDate}
                onChange={(e) => setPromiseDate(e.target.value)}
              />
            </Field>
          ) : (
            <Field label={t.calls.callBack} hint={t.calls.callBackHint}>
              <Input
                type="datetime-local"
                value={callBack}
                onChange={(e) => setCallBack(e.target.value)}
              />
            </Field>
          )}
          <Field label={t.calls.report} className="sm:col-span-2">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            <Button type="submit" disabled={act.isPending}>
              {t.calls.save}
            </Button>
            <ActionError error={act.error} />
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
